// Sprint 33, story-167/169/170. Mirrors backend `daily-site-report.types.ts`/`dto/daily-site-report.dto.ts`.

export enum DailySiteReportTrigger {
  SCHEDULED = 'SCHEDULED',
  MANUAL = 'MANUAL',
}

export enum DailySiteReportEmailStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  SKIPPED_EMPTY = 'SKIPPED_EMPTY',
  SKIPPED_OPTED_OUT = 'SKIPPED_OPTED_OUT',
}

export interface DailySiteReportCounts {
  statusChanges: number;
  tasksCompleted: number;
  incidents: number;
  punchUsers: number;
  notes: number;
  documents: number;
  stockTransactions: number;
  tasksTomorrow: number;
  pendingIncidents: number;
}

export interface DailySiteReportSummary {
  reportId: string;
  organizationId: string;
  userId: string;
  localDate: string;
  timezone: string;
  windowStart: string;
  windowEnd: string;
  trigger: DailySiteReportTrigger;
  emailStatus: DailySiteReportEmailStatus;
  isEmpty: boolean;
  sentAt?: string | null;
  counts: DailySiteReportCounts;
  createdAt: string;
}

export interface Section<T> {
  items: T[];
  truncated: boolean;
}

export interface StatusChangeItem {
  taskId: string;
  taskName: string;
  projectId: string;
  projectName: string;
  previousStatus: string | null;
  newStatus: string;
  at: string;
  userName: string;
}

export interface IncidentItem {
  incidentId: string;
  projectId: string;
  projectName: string;
  incidentType: string | null;
  status: string;
  at: string;
  // Sprint 38, story-205. Optional: reports stored before this sprint lack them.
  name?: string;
  description?: string;
  otherIncidentType?: string;
  delay?: number;
  delayUnit?: string;
  reportedByName?: string;
  ageDays?: number;
}

export interface CrewHourEntry {
  userId: string;
  userName: string;
  hours: number | null;
  adjusted: boolean;
  stillOnSite: boolean;
}

export interface ProjectHours {
  projectId: string;
  projectName: string;
  entries: CrewHourEntry[];
}

export interface NoteItem {
  noteId: string;
  taskId: string | null;
  taskName: string | null;
  projectId: string | null;
  projectName: string | null;
  excerpt: string;
  userName: string;
  at: string;
}

export interface DocumentItem {
  documentId: string;
  filename: string;
  projectId: string | null;
  projectName: string | null;
  uploaderName: string | null;
  at: string;
}

export interface StockTransactionItem {
  transactionId: string;
  type: string;
  materialName: string;
  quantity: number;
  unit: string | null;
  at: string;
}

export interface WarehouseTx {
  warehouseId: string;
  warehouseName: string;
  entries: StockTransactionItem[];
}

export interface TaskItem {
  taskId: string;
  taskName: string;
  projectId: string;
  projectName: string;
  startTime: string | null;
  endTime: string | null;
}

export interface LookingAhead {
  tomorrowISO: string;
  tasksStarting: TaskItem[];
  tasksDue: TaskItem[];
  pendingIncidents: IncidentItem[];
}

export interface DailySiteReportSections {
  schemaVersion: 1;
  role: string;
  scope: { projectCount: number; projectNames: string[] };
  statusChanges: Section<StatusChangeItem>;
  tasksCompleted: Section<StatusChangeItem>;
  incidents: Section<IncidentItem>;
  crewHours: { projects: ProjectHours[] } | null;
  notes: Section<NoteItem>;
  documents: Section<DocumentItem>;
  stock: { warehouses: WarehouseTx[] } | null;
  lookingAhead: LookingAhead | null;
}

export interface DailySiteReport extends DailySiteReportSummary {
  errorMessage?: string | null;
  schemaVersion: number;
  sections: DailySiteReportSections;
}

export interface DailySiteReportSettings {
  available: boolean;
  organizationTimezone?: string | null;
  organizationSendHour: number;
  canEditOrganizationSettings: boolean;
  emailEnabled: boolean;
  manualSendsRemainingToday: number;
  nextScheduledSendAt?: string | null;
  // Sprint 38, story-207. Absent on an older backend: treat as eligible (fail open to the server).
  eligible?: boolean;
  ineligibleReason?: DailySiteReportIneligibleReason | null;
}

export type DailySiteReportIneligibleReason =
  | 'NO_ROLE'
  | 'CLIENT_NO_ACTIVITY_ACCESS'
  | 'CLIENT_NO_LINKED_CLIENT';

export interface RunDailySiteReportsResult {
  organizationsProcessed: number;
  usersScanned: number;
  generated: number;
  alreadyGenerated: number;
  emailed: number;
  skippedEmpty: number;
  skippedOptedOut: number;
  failed: number;
}

export interface DailySiteReportOrganizationSettingsInput {
  organizationId: string;
  dailyReportTimezone?: string | null;
  dailyReportSendHour?: number;
}
